import { Component, computed, EventEmitter, inject, input, InputSignal, OnInit, Output, Signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { QuantityCounterInputModel } from '@libs/form/models';
import { CartQueueItem, CartService } from '@libs/shared/services';
import { CartTrackingService } from '@libs/shared/tracking';
import { ProductModel, ShopProductListItemBaseModel } from '@libs/shop/models';
import { debounceTime } from 'rxjs';
import { ProductBundleListCardComponent, ProductLineItemComponent } from '../../catalog';
import { ConfigurableBundleOrderCardComponent } from '../../configurable-bundles/configurable-bundle-order-card';

@Component({
  selector: 'ui-order-item',
  imports: [
    ProductBundleListCardComponent,
    ProductLineItemComponent,
    ConfigurableBundleOrderCardComponent,
  ],
  templateUrl: './order-item.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './order-item.component.scss',
})
export class OrderItemComponent implements OnInit {
  @Output() public quantityChanged: EventEmitter<ShopProductListItemBaseModel> = new EventEmitter<ShopProductListItemBaseModel>();
  @Output() public removed: EventEmitter<ShopProductListItemBaseModel> = new EventEmitter<ShopProductListItemBaseModel>();
  public cartService: CartService = inject(CartService);
  public cartTrackingService: CartTrackingService = inject(CartTrackingService);
  public product: InputSignal<ProductModel> = input.required();
  public productItem: InputSignal<ShopProductListItemBaseModel> = input.required();
  public readonly: InputSignal<boolean> = input(false);
  public position: InputSignal<number> = input.required();

  public cartQueue: Signal<CartQueueItem[]> = toSignal(this.cartService.getCartQueue(), { initialValue: [] });
  public isQuantityChangeLoading: Signal<boolean> = computed(() => this.cartQueue()
    .filter((item) => item.actionType === 'changeQuantity')
    .findIndex((item) => item.data.sku === this.productItem().sku) > -1);

  public isRemoveLoading: Signal<boolean> = computed(() => this.cartQueue()
    .filter((item) => item.actionType === 'remove')
    .findIndex((item) => item.data.sku === this.productItem().sku) > -1);

  public quantityCounterControl: FormControl<number> = new FormControl(0, { updateOn: 'blur' }) as FormControl<number>;
  public quantityCounterDefaultData: QuantityCounterInputModel = {
    type: 'quantityCounter',
    label: '',
    name: 'quantity',
  };

  public quantityCounterData: Signal<QuantityCounterInputModel> = computed(() => ({
    ...this.quantityCounterDefaultData,
    value: this.productItem().quantity,
  }));

  public ngOnInit(): void {
    this.quantityCounterControl.setValue(this.productItem().quantity);
    this.quantityCounterControl.valueChanges.pipe(debounceTime(800)).subscribe((value: number) => {
      if (this.productItem().quantity === value) {
        return;
      }

      this.quantityChanged.emit({
        ...this.productItem(),
        quantity: value,
      });
    });
  }

  protected onRemoveCartItem(): void {
    this.removed.emit(this.productItem());

    this.cartTrackingService.trackRemoveFromCartProducts([
      {
        ...this.productItem().productData,
        type: 'product',
        position: this.position(),
        quantity: this.productItem().quantity,
      },
    ]);
  }
}
